document.documentElement.classList.remove('no-js');

document.querySelectorAll('.cart-drawer, .search-drawer, .header-mobile-drawer').forEach((drawer) => {
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }
});

class Track50Header extends HTMLElement {
  connectedCallback() {
    const sticky = this.dataset.sticky === 'true';
    const hideOnScroll = this.dataset.scrollBehavior === 'hide_on_scroll';
    let lastY = window.scrollY;

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;

      if (sticky && hideOnScroll) {
        if (currentY > 100 && currentY > lastY) {
          this.classList.add('is-hidden');
        } else {
          this.classList.remove('is-hidden');
        }
      }

      if (currentY > 20) this.classList.add('is-scrolled');
      else this.classList.remove('is-scrolled');

      lastY = currentY;
    }, { passive: true });
  }
}
customElements.define('track50-header', Track50Header);

class Track50Drawer extends HTMLElement {}
customElements.define('track50-drawer', Track50Drawer);

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-drawer-toggle]');
  if (!button) return;
  event.preventDefault();

  const target = button.dataset.drawerTarget;
  const targetEl = target
    ? document.getElementById(target)
    : button.closest('track50-drawer');
  if (!targetEl) return;

  const isOpen = targetEl.classList.toggle('is-open');
  const locksScroll =
    targetEl.classList.contains('header-mobile-drawer') ||
    targetEl.classList.contains('cart-drawer');

  if (locksScroll) {
    const anyOpen = document.querySelector('.header-mobile-drawer.is-open, .cart-drawer.is-open');
    document.documentElement.classList.toggle('drawer-open', !!anyOpen);
    document.body.classList.toggle('drawer-open', !!anyOpen);
  }

  if (targetEl.classList.contains('search-drawer') && isOpen) {
    const input = targetEl.querySelector('.search-drawer__input');
    if (input) {
      window.setTimeout(() => input.focus(), 120);
    }
  }

  if (targetEl.classList.contains('cart-drawer') && isOpen) {
    document.dispatchEvent(new CustomEvent('track50:cart:refresh'));
  }
});

function closeAllDrawers() {
  document
    .querySelectorAll('.search-drawer.is-open, .header-mobile-drawer.is-open, .cart-drawer.is-open')
    .forEach((drawer) => drawer.classList.remove('is-open'));
  document.documentElement.classList.remove('drawer-open');
  document.body.classList.remove('drawer-open');
}

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeAllDrawers();
  }
});

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.transition = this.dataset.transition || 'fade';
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.index = 0;
    this.timer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.slides.length) return;

    if (this.slides.length === 1) {
      this.setActive(0, false);
      return;
    }

    this.bindEvents();
    this.setActive(0, false);
    if (this.autoplayEnabled && !this.reducedMotion) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = parseInt(dot.dataset.heroDot, 10);
        if (!Number.isNaN(target)) this.goTo(target);
      });
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled && !this.reducedMotion) this.startAutoplay();
    });

    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget) && this.autoplayEnabled && !this.reducedMotion) {
        this.startAutoplay();
      }
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      }
    });

    // Swipe gestures for touch devices.
    let touchStartX = 0;
    let touchStartY = 0;
    let touchDX = 0;
    let touchDY = 0;
    let touchActive = false;
    const swipeThreshold = 50;

    this.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchDX = 0;
      touchDY = 0;
      touchActive = true;
      this.stopAutoplay();
    }, { passive: true });

    this.addEventListener('touchmove', (event) => {
      if (!touchActive) return;
      const touch = event.touches[0];
      touchDX = touch.clientX - touchStartX;
      touchDY = touch.clientY - touchStartY;

      // Only prevent default when the gesture is clearly horizontal.
      if (Math.abs(touchDX) > Math.abs(touchDY) && Math.abs(touchDX) > 10) {
        event.preventDefault();
      }
    }, { passive: false });

    this.addEventListener('touchend', () => {
      if (!touchActive) return;
      touchActive = false;

      if (Math.abs(touchDX) >= swipeThreshold && Math.abs(touchDX) > Math.abs(touchDY)) {
        if (touchDX < 0) this.goTo(this.index + 1);
        else this.goTo(this.index - 1);
      }
    });
  }

  goTo(targetIndex) {
    const count = this.slides.length;
    const nextIndex = (targetIndex + count) % count;
    if (nextIndex === this.index) return;
    this.setActive(nextIndex, true);
    if (this.autoplayEnabled && !this.reducedMotion) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  setActive(nextIndex, animate) {
    this.index = nextIndex;

    if (this.transition === 'slide' && this.track) {
      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(-${nextIndex * 100}%, 0, 0)`;
      if (!animate) {
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
    }

    this.slides.forEach((slide, i) => {
      const isActive = i === nextIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === nextIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.tabIndex = isActive ? 0 : -1;
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = window.setInterval(() => this.goTo(this.index + 1), this.speed);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}

customElements.define('hero-slideshow', HeroSlideshow);

(function initScrollReveal() {
  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionDuration = getComputedStyle(html).getPropertyValue('--motion-duration').trim();
  const animationsOff = motionDuration === '0ms' || motionDuration === '0s' || motionDuration === '';
  const inEditor = window.Shopify && window.Shopify.designMode;

  if (reduceMotion || animationsOff || inEditor || !('IntersectionObserver' in window)) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  const staggerSelectors = [
    '.product-grid-section__grid',
    '.fp-carousel__track',
    '.collection-list',
    '.grid',
    '[data-reveal-stagger]',
  ];

  document.querySelectorAll('main .shopify-section').forEach((section) => {
    section.classList.add('t50-reveal');
    observer.observe(section);

    staggerSelectors.forEach((selector) => {
      section.querySelectorAll(selector).forEach((group) => {
        group.classList.add('t50-reveal-stagger');
        Array.from(group.children).forEach((child, index) => {
          child.style.setProperty('--t50-stagger-index', index % 12);
        });
        observer.observe(group);
      });
    });
  });
})();

(function initMobileSubmenuAnimation() {
  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionDuration = getComputedStyle(html).getPropertyValue('--motion-duration').trim();
  const animationsOff = motionDuration === '0ms' || motionDuration === '0s' || motionDuration === '';

  if (reduceMotion || animationsOff) {
    return;
  }

  document.querySelectorAll('.header-mobile-menu details').forEach((details) => {
    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector(':scope > ul');
    if (!summary || !panel) return;

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (details.dataset.animating === '1') return;
      details.dataset.animating = '1';

      const finish = (callback) => {
        panel.addEventListener(
          'transitionend',
          function handler(e) {
            if (e.target !== panel || e.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', handler);
            callback();
            delete details.dataset.animating;
          },
          { once: false }
        );
      };

      if (details.open) {
        panel.style.height = panel.scrollHeight + 'px';
        requestAnimationFrame(() => {
          panel.style.height = '0px';
        });
        finish(() => {
          details.open = false;
          panel.style.height = '';
        });
      } else {
        details.open = true;
        const target = panel.scrollHeight;
        panel.style.height = '0px';
        requestAnimationFrame(() => {
          panel.style.height = target + 'px';
        });
        finish(() => {
          panel.style.height = '';
        });
      }
    });
  });
})();

function setAddButtonLoading(button, isLoading) {
  if (!button) return;
  const label = button.querySelector('.product-card__add-text, [data-add-label]');
  if (isLoading) {
    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) {
      label.setAttribute('hidden', '');
    } else if (!button.dataset.addLabel) {
      button.dataset.addLabel = button.textContent.trim();
      button.textContent = '';
    }
  } else {
    button.classList.remove('is-loading');
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (label) {
      label.removeAttribute('hidden');
    } else if (button.dataset.addLabel) {
      button.textContent = button.dataset.addLabel;
    }
  }
}

(function initAddToCart() {
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    const isAddForm =
      form.matches('form[action$="/cart/add"]') || action.indexOf('/cart/add') !== -1;
    if (!isAddForm) return;

    event.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn?.classList.contains('is-loading')) return;

    const isPdp = form.closest('.pdp-product');
    if (isPdp && submitBtn) {
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    } else {
      setAddButtonLoading(submitBtn, true);
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) throw new Error('Cart add failed');

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
        })
        .catch(() => {});

      document.dispatchEvent(new CustomEvent('track50:cart:refresh'));

      const drawer = document.querySelector('.cart-drawer');
      if (drawer && !drawer.classList.contains('is-open')) {
        drawer.classList.add('is-open');
        document.documentElement.classList.add('drawer-open');
        document.body.classList.add('drawer-open');
      }
    } catch (e) {
      if (isPdp && submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      } else {
        setAddButtonLoading(submitBtn, false);
      }
      form.submit();
      return;
    }

    if (isPdp && submitBtn) {
      submitBtn.classList.remove('is-loading');
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-busy');
    } else {
      setAddButtonLoading(submitBtn, false);
    }
  });
})();

(function initCartDrawer() {
  const drawer = document.querySelector('.cart-drawer');
  if (!drawer) return;

  const content = drawer.querySelector('[data-cart-content]');
  const sectionId = drawer.dataset.sectionId;
  const cartUrl = drawer.dataset.cartUrl || '/cart';
  const moneyFormat = drawer.dataset.moneyFormat || '${{amount}}';
  let isLoading = false;

  const formatCartMoney = (cents) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2);
  };

  const updateCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    const titleEl = drawer.querySelector('[data-cart-drawer-title]');
    if (titleEl) {
      const base = titleEl.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      titleEl.textContent = count > 0 ? `${base} (${count})` : base;
    }
  };

  const setBusy = (busy) => {
    isLoading = busy;
    drawer.classList.toggle('is-busy', busy);
  };

  const refresh = async () => {
    if (!sectionId) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        updateCount(cart.item_count);
        if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
      } catch (e) {
        /* ignore */
      }
      return;
    }

    try {
      const res = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const data = await res.json();
      const html = data[sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot = doc.getElementById(`shopify-section-${sectionId}`) || doc.body;
      const newContent = sectionRoot.querySelector('[data-cart-content]');
      const newShipping = sectionRoot.querySelector('[data-free-shipping][data-cart-drawer-shipping]');
      const newTitle = sectionRoot.querySelector('[data-cart-drawer-title]');

      if (newContent && content) {
        content.innerHTML = newContent.innerHTML;
      }
      const shippingSlot = drawer.querySelector('[data-cart-drawer-shipping]')?.parentElement;
      const headShippingArea = drawer.querySelector('.cart-drawer__head');
      const existingShipping = drawer.querySelector('[data-free-shipping][data-cart-drawer-shipping]');
      if (newShipping && headShippingArea) {
        const shippingNode = document.importNode(newShipping, true);
        if (existingShipping) {
          existingShipping.replaceWith(shippingNode);
        } else {
          headShippingArea.appendChild(shippingNode);
        }
      } else if (existingShipping) {
        existingShipping.remove();
      }
      const titleEl = drawer.querySelector('[data-cart-drawer-title]');
      if (newTitle && titleEl) {
        titleEl.textContent = newTitle.textContent;
      }

      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      updateCount(cart.item_count);
      if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
      if (window.Track50CartDiscount) window.Track50CartDiscount.updateFromCart(cart);
      bindDrawerEvents();
    } catch (e) {
      /* ignore */
    }
  };

  const changeQty = async (key, quantity) => {
    if (isLoading) return;
    setBusy(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await res.json();
      updateCount(cart.item_count);
      if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
      await refresh();
    } catch (e) {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  function bindDrawerEvents() {
    if (!content || content.dataset.cartEventsBound === 'true') return;
    content.dataset.cartEventsBound = 'true';

    content.addEventListener('click', (event) => {
      const item = event.target.closest('[data-cart-item]');
      if (!item) return;
      const key = item.dataset.key;
      const qtyEl = item.querySelector('.cart-drawer-item__qty-value');
      const current = parseInt(qtyEl?.textContent, 10) || 0;

      if (event.target.closest('[data-qty-up]')) {
        event.preventDefault();
        changeQty(key, current + 1);
      } else if (event.target.closest('[data-qty-down]')) {
        event.preventDefault();
        changeQty(key, Math.max(0, current - 1));
      } else if (event.target.closest('[data-remove]')) {
        event.preventDefault();
        changeQty(key, 0);
      }
    });
  }

  bindDrawerEvents();
  document.addEventListener('track50:cart:refresh', refresh);
})();

function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const amount = Number(cents) / 100;
  const formatString = format || '${{amount}}';
  const match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!match) return amount.toFixed(2);

  const withDelimiters = (num, precision, thousands, decimal) => {
    const fixed = (num / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return precision > 0 ? `${dollars}${decimal}${parts[1] || '00'}` : dollars;
  };

  let value = '';
  switch (match[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }
  return formatString.replace(match[0], value);
}
window.formatMoney = formatMoney;

(function initProductCards() {
  const cards = document.querySelectorAll('[data-product-detail]:not(.pdp-product)');
  if (!cards.length) return;


  cards.forEach((card) => {
    const jsonEl = card.querySelector('[data-product-json]');
    const form = card.querySelector('form[action*="/cart/add"], form[action*="cart"]');
    const variantInput = card.querySelector('[data-variant-id]');
    const priceRoot = card.querySelector('[data-price-root]');
    const addBtn = card.querySelector('[data-add-to-cart]') || form?.querySelector('[type="submit"]');
    const saleTag = card.querySelector('[data-product-sale-tag]');
    const imageWrap = card.querySelector('[data-product-image-wrap]');
    const picker = card.querySelector('[data-variant-picker]');
    if (!jsonEl || !variantInput) return;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    const moneyFormat = card.dataset.moneyFormat || '${{amount}}';
    const variants = product.variants || [];
    const offTemplate = card.dataset.strOff || 'PERCENT% off';
    const saveTemplate = card.dataset.strSave || 'You save AMOUNT';
    const formatOff = (percent) => offTemplate.replace('PERCENT', String(percent));
    const formatSave = (amount) => saveTemplate.replace('AMOUNT', amount);

    const getSelectedOptions = () => {
      if (!picker) return [];
      const inputs = [...picker.querySelectorAll('input[type="radio"]:checked')];
      const options = new Array(product.options.length).fill(null);
      inputs.forEach((input) => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        if (!Number.isNaN(idx)) options[idx] = input.dataset.optionValue;
      });
      return options;
    };

    const findVariant = (options) =>
      variants.find((variant) =>
        variant.options.every((opt, index) => opt === options[index])
      );

    const updatePrice = (variant) => {
      if (!priceRoot || !variant) return;
      const price = variant.price;
      const compareAt = variant.compare_at_price || 0;
      const onSale = compareAt > price;
      const percentOff = onSale
        ? Math.round(((compareAt - price) / compareAt) * 100)
        : 0;
      const savings = onSale ? compareAt - price : 0;

      priceRoot.dataset.price = price;
      priceRoot.dataset.compareAt = compareAt;
      priceRoot.dataset.onSale = onSale;
      priceRoot.dataset.percentOff = percentOff;
      priceRoot.classList.toggle('price--on-sale', onSale);

      const compareEl = priceRoot.querySelector('[data-compare-display]');
      const currentEl = priceRoot.querySelector('[data-current-display]');
      const badgeEl = priceRoot.querySelector('[data-sale-badge]');
      const saveEl = priceRoot.querySelector('[data-save-display]');

      if (compareEl) {
        compareEl.textContent = formatMoney(compareAt, moneyFormat);
        compareEl.hidden = !onSale;
      }
      if (currentEl) currentEl.textContent = formatMoney(price, moneyFormat);
      if (badgeEl) {
        if (onSale && percentOff > 0) {
          badgeEl.textContent = formatOff(percentOff);
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }
      if (saveEl) {
        if (onSale && savings > 0) {
          saveEl.textContent = formatSave(formatMoney(savings, moneyFormat));
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      if (saleTag) {
        if (onSale && percentOff > 0) {
          saleTag.textContent = formatOff(percentOff);
          saleTag.hidden = false;
        } else {
          saleTag.hidden = true;
        }
      }
    };

    const updateImage = (variant) => {
      if (!imageWrap) return;
      const img = imageWrap.querySelector('img');
      if (!img) return;
      const src =
        variant.featured_image?.src ||
        variant.featured_media?.preview_image?.src ||
        product.featured_image;
      if (src) {
        img.src = src;
        img.srcset = '';
      }
    };

    const updateVariant = () => {
      const options = getSelectedOptions();
      const variant = findVariant(options) || variants[0];
      if (!variant) return;

      variantInput.value = variant.id;
      updatePrice(variant);
      updateImage(variant);

      if (addBtn) {
        const labelEl = addBtn.querySelector('span');
        if (variant.available) {
          addBtn.removeAttribute('disabled');
          addBtn.type = 'submit';
          const label =
            form?.dataset.labelAdd || addBtn.dataset.labelAdd || 'Add to cart';
          if (labelEl) labelEl.textContent = label;
          else addBtn.textContent = label;
        } else {
          addBtn.setAttribute('disabled', '');
          addBtn.type = 'button';
          const soldOut =
            form?.dataset.labelSoldOut || addBtn.dataset.labelSoldOut || 'Sold out';
          if (labelEl) labelEl.textContent = soldOut;
          else addBtn.textContent = soldOut;
        }
      }
    };

    if (picker) {
      picker.addEventListener('change', updateVariant);
    }
    updateVariant();
  });
})();
